// src/modules/assetManagement/library/LibraryAssets.tsx
// Library › Assets: browse everything stored, filter by event, product or tag, link to Events/Products. No file actions here.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, FolderCog, Image as ImageIcon, LayoutGrid, Link2, List, Package, Tag } from "lucide-react";
import { assetApi } from "../assetApi";
import AssetThumb from "../components/AssetThumb";
import { LinkModal } from "../components/AssetModals";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { Button, EmptyState, SearchInput, StatusBadge, StorageBadge, useToast } from "../components/ui";
import type { Asset, Crumb } from "../types";
import { useAssetFilterOptions } from "../useAssetFilterOptions";
import { useMockQuery } from "../useMockQuery";
import { ENTITY, formatBytes, formatDate, ROUTES } from "../utils";

const SELF: Crumb = { label: "Assets", to: ROUTES.libraryAssets };

const LinkCounts: React.FC<{ asset: Asset }> = ({ asset }) => (
  <div className="flex items-center gap-1.5">
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${ENTITY.event.chip}`} title="Linked events">
      <CalendarDays size={11} /> {asset.eventIds.length}
    </span>
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${ENTITY.product.chip}`} title="Linked products">
      <Package size={11} /> {asset.productIds.length}
    </span>
  </div>
);

const LibraryAssets: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const search = params.get("q") ?? "";
  const tags = params.getAll("tag");
  const eventIds = params.getAll("event");
  const productIds = params.getAll("product");
  const view = params.get("view") === "list" ? "list" : "grid";
  const [searchInput, setSearchInput] = useState(search);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linkOpen, setLinkOpen] = useState(false);
  const { toastNode, showToast } = useToast();
  const navigate = useNavigate();

  // Shortcut: press M to open Asset Management (ignored while typing or with a dialog open)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "m" || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      navigate(ROUTES.management);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  const updateParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  // Debounce the search box into the URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) updateParams((p) => (searchInput ? p.set("q", searchInput) : p.delete("q")));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const filterKey = [tags, eventIds, productIds].map((x) => x.join("|")).join("~");
  const { data: assets = [], initialLoading } = useMockQuery(
    () => assetApi.listAssets({ search, tags, eventIds, productIds }),
    [search, filterKey],
  );
  const { eventOptions, productOptions, tagOptions, productNote } = useAssetFilterOptions(eventIds, productIds);

  // Drop selections that are no longer visible
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set(
        [...prev].filter((id) => assets.some((asset) => asset.id === id))
      );

      // Avoid unnecessary state updates and infinite render loops
      if (
        prev.size === next.size &&
        [...prev].every((id) => next.has(id))
      ) {
        return prev;
      }

      return next;
    });
  }, [assets]);

  const selectedAssets = useMemo(() => assets.filter((a) => selected.has(a.id)), [assets, selected]);
  const allSelected = assets.length > 0 && assets.every((a) => selected.has(a.id));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Replace every value of a repeated URL param */
  const setMulti = (key: string, values: string[]) =>
    updateParams((p) => {
      p.delete(key);
      values.forEach((v) => p.append(key, v));
    });
  const hasFilters = tags.length + eventIds.length + productIds.length > 0 || !!search;

  return (
    <div className="space-y-4">
      {toastNode}

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-3">
        <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Search name, tag, event or product" className="w-72" />
        <MultiSelectFilter noun="events" icon={<CalendarDays size={15} />} options={eventOptions} selected={eventIds} onChange={(v) => setMulti("event", v)} />
        <MultiSelectFilter
          noun="products"
          icon={<Package size={15} />}
          options={productOptions}
          selected={productIds}
          onChange={(v) => setMulti("product", v)}
          note={productNote}
        />
        <MultiSelectFilter noun="tags" icon={<Tag size={15} />} options={tagOptions} selected={tags} onChange={(v) => setMulti("tag", v)} className="w-40" />
        {hasFilters && (
          <button
            type="button"
            className="text-sm text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
            onClick={() => {
              setSearchInput("");
              updateParams((p) => ["tag", "event", "product", "q"].forEach((k) => p.delete(k)));
            }}
          >
            Clear filters
          </button>
        )}
        <div className="ml-auto flex gap-1 bg-slate-100 rounded-lg p-1">
          {(["grid", "list"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => updateParams((p) => (v === "list" ? p.set("view", "list") : p.delete("view")))}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${view === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
            >
              {v === "grid" ? <LayoutGrid size={14} /> : <List size={14} />}
              {v === "grid" ? "Grid" : "List"}
            </button>
          ))}
        </div>
      </div>

      {/* Selection bar */}
      <div className="flex flex-wrap items-center gap-3 min-h-[38px] text-sm">
        {assets.length > 0 && (
          <label className="inline-flex items-center gap-2 text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 accent-slate-700"
              checked={allSelected}
              onChange={() => setSelected(allSelected ? new Set() : new Set(assets.map((a) => a.id)))}
            />
            Select all
          </label>
        )}
        <span className="text-slate-500">
          {assets.length} asset{assets.length === 1 ? "" : "s"}
          {selected.size > 0 && <span className="text-slate-800 font-medium"> · {selected.size} selected</span>}
        </span>
        {selected.size > 0 && (
          <>
            <Button size="sm" variant="primary" icon={<Link2 size={14} />} onClick={() => setLinkOpen(true)}>
              Link to Events or Products
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </>
        )}
        <Link
          to={ROUTES.management}
          title="Upload, archive, share or delete in Asset Management (shortcut: M)"
          className="ml-auto inline-flex items-center gap-2 pl-4 pr-2 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-90 transition"
          style={{ backgroundColor: "var(--color-primary, #2563EB)" }}
        >
          <FolderCog size={16} /> Go to Asset Management
          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-md bg-white/25 text-[11px] font-bold leading-none font-sans">
            M
          </span>
        </Link>
      </div>

      {/* Results */}
      {initialLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-48 rounded-xl bg-slate-200/60 animate-pulse" />
          ))}
        </div>
      ) : assets.length === 0 ? (
        <EmptyState icon={<ImageIcon size={22} />} title="No assets match" hint="Try other filters or clear them." />
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {assets.map((a) => {
            const isSel = selected.has(a.id);
            return (
              <div
                key={a.id}
                className={`group relative bg-white rounded-xl border overflow-hidden transition hover:shadow-md ${isSel ? "border-slate-700 ring-2 ring-slate-300" : "border-slate-200"
                  }`}
              >
                <label className="absolute top-2 left-2 z-10 h-6 w-6 rounded-md bg-white/95 shadow flex items-center justify-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-slate-700"
                    checked={isSel}
                    onChange={() => toggle(a.id)}
                    aria-label={`Select ${a.filename}`}
                  />
                </label>
                <Link to={ROUTES.asset(a.id)} state={{ trail: [SELF] }} className="block">
                  <div className="h-32 bg-slate-100 overflow-hidden">
                    <AssetThumb asset={a} />
                  </div>
                  <div className="p-3 space-y-2">
                    <p className="text-sm font-medium text-slate-800 truncate" title={a.filename}>
                      {a.filename}
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <LinkCounts asset={a} />
                      {a.status !== "completed" ? <StatusBadge status={a.status} /> : <StorageBadge storage={a.storage} hideActive />}
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="w-10 px-4 py-3" />
                <th className="text-left px-2 py-3 font-semibold">Asset</th>
                <th className="text-left px-4 py-3 font-semibold">Tags</th>
                <th className="text-left px-4 py-3 font-semibold">Links</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Uploaded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {assets.map((a) => (
                <tr key={a.id} className={selected.has(a.id) ? "bg-slate-50" : "hover:bg-slate-50"}>
                  <td className="px-4 py-2.5">
                    <input type="checkbox" className="h-4 w-4 accent-slate-700" checked={selected.has(a.id)} onChange={() => toggle(a.id)} aria-label={`Select ${a.filename}`} />
                  </td>
                  <td className="px-2 py-2.5">
                    <Link to={ROUTES.asset(a.id)} state={{ trail: [SELF] }} className="flex items-center gap-3 min-w-0 group">
                      <span className="w-12 h-9 rounded-md overflow-hidden bg-slate-100 shrink-0">
                        <AssetThumb asset={a} size="sm" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium text-slate-800 truncate group-hover:underline max-w-[320px]">{a.filename}</span>
                        <span className="block text-xs text-slate-500">{formatBytes(a.sizeBytes)}</span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {a.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-xs text-slate-500">#{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><LinkCounts asset={a} /></td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <StatusBadge status={a.status} />
                      <StorageBadge storage={a.storage} hideActive />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{formatDate(a.uploadedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {linkOpen && (
        <LinkModal
          assets={selectedAssets}
          onClose={() => setLinkOpen(false)}
          onDone={(msg) => {
            setLinkOpen(false);
            setSelected(new Set());
            showToast(msg);
          }}
        />
      )}
    </div>
  );
};

export default LibraryAssets;